import { defineStore } from "pinia";
import { toast } from "vue3-toastify";
import { EventConstants } from "~/assets/constants/ecomApi";
import { ProductService } from "~/services/product.service";
import type { APIState } from "~/types/api-state.type";
import type { Product } from "~/types/ecom/product/product.type";

const myList = "Mijn lijst";
const saveForLater = "Bewaar voor later";
export const useSavelistStore = defineStore({
  id: "savelist",
  state: () => ({
    // accountStore: useAccountStore(),

    /**
     * This is the core state handler. This will sit on the parent level.
     * It depends on the ping status of savelist endpoint group.
     * saved-lists/_ping
     */
    savelist_page_state: <APIState>{
      status: "loading",
      message: "Page is idle",
    },

    savelist_api_healthy: <boolean>false,

    /**
     * This is the state handler for the user savelist section
     */
    user_savelists_state: <APIState>{
      status: "success",
      message: "Page is idle",
    },

    user_savelists_modal_state: <APIState>{
      status: "success",
      message: "Page is idle",
    },

    user_savelists: <any>[],

    first_fetch: <boolean>true,

    selected_savelist_state: <APIState>{
      status: "idle",
      message: "Component is idle",
    },

    /**
     * This is the state handler for the selected savelist's item.
     */
    selected_savelist_item_state: <APIState>{
      status: "loading",
      message: "Page is idle",
    },

    current_active_savelist: {
      name: <string>"Loading...",
      id: <string>"",
      items: <object[] | []>[
        {
          product_id: "",
          quantity: "",
        },
      ],
      internal: <boolean>false,
      index: <number>0,
      is_default: false, // New key to track if this is the default list
    },

    current_active_product: <Product | null>null,
    products_in_current_savelist: <Product[]>[],

    lastUpdated: <number | null>null,
    /**
     * Probably an unique id idk what I'll use for this, and what is something that stays on the front end only
     */
    default_list: "",

    add_new_list_modal: <boolean>false,
    edit_list_modal: <boolean>false,
    delete_list_modal: <boolean>false,
    remove_product_modal: <boolean>false,

    modal_savelist_from_visible: <boolean>false,

    savelist_item_to_delete: {
      name: <string>"",
      product_code: <string>"",
    },

    consolidated_product_code_list: <any>[],

    selectedSavelist: <string[]>[],

    my_list_exists: false,
    save_for_later_exists: false,

    user_save_for_later_id: <string>"",
    my_list_id: <string>"",

    fetching_in_progress: <boolean>false,
    tab_clicked_array: <String[]>[],
    savelist_share_modal_visible: false,
    encoded_savelist_url: "",
    modal_savelist_multiple_items_visible: <boolean>false,
  }),
  actions: {
    /**
     * Saved lists endpoint health check method
     */
    async verifySaveListHealth() {
      const { verifySaveListHealth } = await import('~/stores-datalayer/savelist.datalayer');
      return await verifySaveListHealth();
    },

    async getUserSaveLists(forceFetch?: boolean, newIndex?: number) {
      const { getUserSaveLists } = await import('~/stores-datalayer/savelist.datalayer');
      return await getUserSaveLists(forceFetch,newIndex);
    },

    async makeDefault(savelistId: string): Promise<void> {
      const { makeDefault } = await import('~/stores-datalayer/savelist.datalayer');
      return await makeDefault(savelistId);
    },

    async validateAndCreateDefaultLists(listName: string) {
      const { validateAndCreateDefaultLists } = await import('~/stores-datalayer/savelist.datalayer');
      return await validateAndCreateDefaultLists(listName);
    },

    async checkIfDefaultSavelistExists(checkSavelistName: string) {
      const { checkIfDefaultSavelistExists } = await import('~/stores-datalayer/savelist.datalayer');
      return checkIfDefaultSavelistExists(checkSavelistName);
    },

    async extractSaveForLaterId() {
      const { extractSaveForLaterId } = await import('~/stores-datalayer/savelist.datalayer');
      return extractSaveForLaterId();
    },

    async extractMyListId() {
      const { extractMyListId } = await import('~/stores-datalayer/savelist.datalayer');
      return extractMyListId();
    },

    async createDefaultSavelists(listName: string) {
      const { createDefaultSavelists } = await import('~/stores-datalayer/savelist.datalayer');
      return await createDefaultSavelists(listName);
    },

    async addItemToSaveForLater(
      product: {
        product_code: string;
        quantity: string | number;
      },
      showNotification: boolean = true,
      bulkAdditon: boolean = false
    ) {
      const { addItemToSaveForLater } = await import('~/stores-datalayer/savelist.datalayer');
      return await addItemToSaveForLater(product,showNotification,bulkAdditon);
    },

    async addItemToMyList(
      product: {
        product_code: string;
        quantity: string | number;
      },
    ) {
      const { addItemToMyList } = await import('~/stores-datalayer/savelist.datalayer');
      return await addItemToMyList(product);
    },

   async markSavelistAsInternal() {
      const { markSavelistAsInternal } = await import('~/stores-datalayer/savelist.datalayer');
      return markSavelistAsInternal();
    },

    async createNewList(newListName: string, makeDefault: boolean) {
      const { createNewList } = await import('~/stores-datalayer/savelist.datalayer');
      return await createNewList(newListName, makeDefault);
    },

    async deleteSavedlist() {
      const { deleteSavedlist } = await import('~/stores-datalayer/savelist.datalayer');
      return await deleteSavedlist();
    },
    async editSavedlist(updateListName: string, makeDefault: boolean) {
      const { editSavedlist } = await import('~/stores-datalayer/savelist.datalayer');
      return await editSavedlist(updateListName,makeDefault);
    },

    async addItemToSavelist(
      savelistId: string,
      productCode: string,
      quantity?: string,
      showToast: boolean = true
    ) {
      const authStore = useAuthStore();
      const authToken = authStore.user?.token;

      try {
        const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
        const response = await useAjaxEcom(`/saved-lists/${savelistId}/items`, {
          headers: {
            "X-Toolstation-Customer-Id": authToken,
          },
          params: {
            product_code: productCode,
            quantity: quantity ?? "1",
          },
          method: "post",
        });
        this.add_new_list_modal = false;

        if (!response.data) {
          throw new Error("Couldn't add product to savelist.");
        }
        if (showToast) {
          toast(useTranslation('ToastAccountSaveList-successfullyAddedToList', 'Succesvol toegevoegd aan de lijst'), {
            autoClose: true,
            type: "success",
          });
        }
        await this.getUserSaveLists(true);
        const searchResultStore = useSearchResultStore();
        searchResultStore.updateIsSavedStatus(productCode, true);
        const { logAddToWishlistEvent } = await import('~/stores-datalayer/savelist.datalayer');
        logAddToWishlistEvent(this.current_active_product, productCode, savelistId);
      } catch (err) {
        if (showToast) {
          toast(`${err}`, {
            autoClose: true,
            type: "error",
          });
          trackError("toast", `${err}`);
        }
        this.add_new_list_modal = false;
        const { $customClientErrorHandler } = useNuxtApp();
        const errorMeta = {productCode, savelistId,savelistError: true}
        
        $customClientErrorHandler(
          err,
          'critical',
          'Savelist Store Error - trying to add item to save list using addItemToSavelist()',
          errorMeta
        );
        return err
      }
    },
    async deleteItemFromSavelist(
      savelistId: string,
      productCode: string,
      showToast: boolean = true
    ) {
      const { deleteItemFromSavelist } = await import('~/stores-datalayer/savelist.datalayer');
      return await deleteItemFromSavelist(savelistId,productCode,showToast);
    },

    async updateCurrentSavelist(
      newLabel: string,
      newId: string,
      newItems: [],
      newInternal: boolean,
      newIndex: number,
      newIsDefault: boolean
    ) {
      const { updateCurrentSavelist } = await import('~/stores-datalayer/savelist.datalayer');
      return updateCurrentSavelist(newLabel,newId,newItems,newInternal,newIndex,newIsDefault);
    },

    async getSavelistItemDetails(items: object[], id?: string) {
      const { getSavelistItemDetails } = await import('~/stores-datalayer/savelist.datalayer');
      return await getSavelistItemDetails(items,id);
    },

    async getSavelistShareCode() {
      const { getSavelistShareCode } = await import('~/stores-datalayer/savelist.datalayer');
      return await getSavelistShareCode();
    },

    async getEncodeSavelistCodeQueryString(SavelistCode: string) {
      const { getEncodeSavelistCodeQueryString } = await import('~/stores-datalayer/savelist.datalayer');
      return  getEncodeSavelistCodeQueryString(SavelistCode);
    },

    async extractProductCodes(items: object[]): Promise<string[]> {
      const { extractProductCodes } = await import('~/stores-datalayer/savelist.datalayer');
      return  extractProductCodes(items);
    },

    async bakeConsolidatedItemList(savelists: []) {
      const { bakeConsolidatedItemList } = await import('~/stores-datalayer/savelist.datalayer');
      return  bakeConsolidatedItemList(savelists);
    },

    async populateSavedList() {
      const { populateSavedList } = await import('~/stores-datalayer/savelist.datalayer');
      return  populateSavedList();
    },

    //  This can be used if we want to show confirmation Modal in PLP
    isProductInAnySaveList(): boolean {
        return this.consolidated_product_code_list.some(
          (item: any) => item.product_code === this.current_active_product?.code
        );
    },
    
    async resetAllModalStates() {
      const { resetAllModalStates } = await import('~/stores-datalayer/savelist.datalayer');
      return  resetAllModalStates();
    },

    async isThisProductSaved(product_code: string): Promise<boolean> {
      const { isThisProductSaved } = await import('~/stores-datalayer/savelist.datalayer');
      return  await isThisProductSaved(product_code);
    },

    async destroyUserSavelist() {
      const { destroyUserSavelist } = await import('~/stores-datalayer/savelist.datalayer');
      return destroyUserSavelist();
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSavelistStore, import.meta.hot));
}
